import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Printer, Download, RefreshCw, Send, Plus, Trash2 } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const CustomsDeclarationFormPage = () => {
  const { toast } = useToast();
  const signatureRef = useRef(null);
  const printRef = useRef(null);
  
  const [sender, setSender] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    province: '',
    country: '',
    postalCode: ''
  });

  const [receiver, setReceiver] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    province: '',
    country: '',
    postalCode: ''
  });

  const [items, setItems] = useState([
    { id: 1, description: '', quantity: '', value: '' },
    { id: 2, description: '', quantity: '', value: '' },
    { id: 3, description: '', quantity: '', value: '' },
    { id: 4, description: '', quantity: '', value: '' },
    { id: 5, description: '', quantity: '', value: '' },
    { id: 6, description: '', quantity: '', value: '' }
  ]);

  const [currency, setCurrency] = useState('CAD');
  const [agreed, setAgreed] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const addItem = () => {
    if (items.length >= 15) {
      toast({
        title: "Maximum Items Reached",
        description: "To fit on one page, maximum 15 items allowed. Consider multiple forms.",
        variant: "destructive"
      });
      return;
    }
    setItems([...items, { id: Date.now(), description: '', quantity: '', value: '' }]);
  };

  const removeItem = (id) => {
    if (items.length <= 1) {
      toast({
        title: "Cannot Remove",
        description: "At least one item must remain.",
        variant: "destructive"
      });
      return;
    }
    setItems(items.filter(item => item.id !== id));
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const value = parseFloat(item.value) || 0;
      return sum + value;
    }, 0).toFixed(2);
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const clearForm = () => {
    if (!window.confirm('Are you sure you want to clear all form data?')) return;
    
    setSender({
      fullName: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      province: '',
      country: '',
      postalCode: ''
    });
    setReceiver({
      fullName: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      province: '',
      country: '',
      postalCode: ''
    });
    setItems([
      { id: 1, description: '', quantity: '', value: '' },
      { id: 2, description: '', quantity: '', value: '' },
      { id: 3, description: '', quantity: '', value: '' }
    ]);
    setAgreed(false);
    setCustomerName('');
    signatureRef.current?.clear();
    
    toast({
      title: "Form Cleared",
      description: "All fields have been reset."
    });
  };

  const validateForm = () => {
    if (!sender.fullName || !sender.phone || !sender.address) {
      toast({
        title: "Missing Sender Information",
        description: "Please complete all required sender fields.",
        variant: "destructive"
      });
      return false;
    }

    if (!receiver.fullName || !receiver.phone || !receiver.address) {
      toast({
        title: "Missing Receiver Information",
        description: "Please complete all required receiver fields.",
        variant: "destructive"
      });
      return false;
    }

    const hasItems = items.some(item => item.description && item.quantity && item.value);
    if (!hasItems) {
      toast({
        title: "No Items Declared",
        description: "Please add at least one item to your shipment contents.",
        variant: "destructive"
      });
      return false;
    }

    if (!agreed) {
      toast({
        title: "Declaration Required",
        description: "Please read and agree to the declaration.",
        variant: "destructive"
      });
      return false;
    }

    if (!customerName) {
      toast({
        title: "Name Required",
        description: "Please enter your full name for the declaration.",
        variant: "destructive"
      });
      return false;
    }

    if (signatureRef.current?.isEmpty()) {
      toast({
        title: "Signature Required",
        description: "Please sign the declaration.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const signatureData = signatureRef.current?.toDataURL();
    
    try {
      const formData = new FormData();
      
      // Customer/Sender Info
      formData.append('customerName', sender.fullName);
      formData.append('phone', sender.phone);
      formData.append('email', sender.email);
      formData.append('senderAddress', sender.address);
      formData.append('cityProvince', `${sender.city}, ${sender.province}`);
      formData.append('postalCode', sender.postalCode);
      formData.append('country', sender.country);
      
      // Recipient Info
      formData.append('recipientName', receiver.fullName);
      formData.append('recipientPhone', receiver.phone);
      formData.append('deliveryAddress', receiver.address);
      formData.append('recipientCity', receiver.city);
      formData.append('recipientState', receiver.province);
      formData.append('recipientPostal', receiver.postalCode);
      formData.append('recipientCountry', receiver.country);
      
      // Shipment Details
      formData.append('numPackages', '1'); // Default, can be added to form if needed
      formData.append('actualWeight', 'See item descriptions');
      formData.append('dimensions', 'Various');
      formData.append('declaredValue', `${currency} ${calculateTotal()}`);
      
      // Contents - format all items as a single string
      const contentsText = items
        .filter(item => item.description)
        .map(item => `${item.description} (Qty: ${item.quantity}, Value: ${currency} ${item.value})`)
        .join('; ');
      formData.append('contents', contentsText);
      
      // ID & Payment (defaults for now - can be enhanced later)
      formData.append('idType', 'Not provided');
      formData.append('issuingCountry', sender.country);
      formData.append('paymentBy', 'sender');
      
      // Agreement & Signature
      formData.append('agreementCustomerName', customerName || sender.fullName);
      formData.append('agreementDate', date);
      formData.append('signatureType', 'draw');
      formData.append('signatureData', signatureData || '');

      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      if (!apiUrl) {
        toast({
          title: "Configuration Error",
          description: "Backend API is not configured. Please contact support.",
          variant: "destructive"
        });
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/customs-declaration`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "Declaration Submitted Successfully!",
          description: "Your customs declaration has been submitted to Shippzy and will be processed shortly."
        });
      } else {
        throw new Error(result.detail || 'Submission failed');
      }
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.message || "We couldn't submit your declaration. Please try again or contact Shippzy.",
        variant: "destructive"
      });
    }
  };

  const handlePrint = () => {
    if (!validateForm()) return;
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!validateForm()) return;

    const element = printRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Customs-Declaration-${Date.now()}.pdf`);

    toast({
      title: "PDF Downloaded",
      description: "Your customs declaration has been saved."
    });
  };

  return (
    <>
      <Helmet>
        <title>Shippzy Customs Declaration Form | International Shipping</title>
        <meta name="description" content="Complete your Shippzy customs declaration online. Enter shipment details, declare contents, and print your official customs form." />
        <link rel="canonical" href="https://shippzy.ca/customs-declaration" />
      </Helmet>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-form, #printable-form * {
            visibility: visible;
          }
          #printable-form {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          button, nav, footer, .action-buttons {
            display: none !important;
          }
        }
        .print-only {
          display: none;
        }
      `}</style>

      {/* Web View */}
      <div className="min-h-screen bg-gray-50 py-8 px-4 no-print">
        <div className="max-w-5xl mx-auto">
          <Card className="shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Shippzy Customs Declaration Form
                </h1>
                <p className="text-gray-600">
                  Complete your shipment information and customs declaration online. Review your details and print a completed copy.
                </p>
              </div>

              {/* Sender Details */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-navy-900 mb-4 border-b-2 border-red-600 pb-2">
                  SECTION A — SENDER DETAILS
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <Input
                      value={sender.fullName}
                      onChange={(e) => setSender({...sender, fullName: e.target.value})}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <Input
                      value={sender.phone}
                      onChange={(e) => setSender({...sender, phone: e.target.value})}
                      placeholder="+1 (123) 456-7890"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <Input
                      type="email"
                      value={sender.email}
                      onChange={(e) => setSender({...sender, email: e.target.value})}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Address *</label>
                    <Input
                      value={sender.address}
                      onChange={(e) => setSender({...sender, address: e.target.value})}
                      placeholder="123 Main Street, Unit 4"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <Input
                      value={sender.city}
                      onChange={(e) => setSender({...sender, city: e.target.value})}
                      placeholder="Toronto"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Province/State *</label>
                    <Input
                      value={sender.province}
                      onChange={(e) => setSender({...sender, province: e.target.value})}
                      placeholder="Ontario"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                    <Input
                      value={sender.country}
                      onChange={(e) => setSender({...sender, country: e.target.value})}
                      placeholder="Canada"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal/ZIP Code *</label>
                    <Input
                      value={sender.postalCode}
                      onChange={(e) => setSender({...sender, postalCode: e.target.value})}
                      placeholder="M5H 2N2"
                    />
                  </div>
                </div>
              </div>

              {/* Receiver Details */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-navy-900 mb-4 border-b-2 border-red-600 pb-2">
                  SECTION B — RECEIVER DETAILS
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <Input
                      value={receiver.fullName}
                      onChange={(e) => setReceiver({...receiver, fullName: e.target.value})}
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <Input
                      value={receiver.phone}
                      onChange={(e) => setReceiver({...receiver, phone: e.target.value})}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <Input
                      type="email"
                      value={receiver.email}
                      onChange={(e) => setReceiver({...receiver, email: e.target.value})}
                      placeholder="jane@example.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Address *</label>
                    <Input
                      value={receiver.address}
                      onChange={(e) => setReceiver({...receiver, address: e.target.value})}
                      placeholder="456 Park Road, Apartment 7B"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <Input
                      value={receiver.city}
                      onChange={(e) => setReceiver({...receiver, city: e.target.value})}
                      placeholder="Mumbai"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State/Province *</label>
                    <Input
                      value={receiver.province}
                      onChange={(e) => setReceiver({...receiver, province: e.target.value})}
                      placeholder="Maharashtra"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                    <Input
                      value={receiver.country}
                      onChange={(e) => setReceiver({...receiver, country: e.target.value})}
                      placeholder="India"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal/ZIP Code *</label>
                    <Input
                      value={receiver.postalCode}
                      onChange={(e) => setReceiver({...receiver, postalCode: e.target.value})}
                      placeholder="400001"
                    />
                  </div>
                </div>
              </div>

              {/* Shipment Contents */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-navy-900 mb-4 border-b-2 border-red-600 pb-2">
                  SECTION C — SHIPMENT CONTENTS
                </h2>
                <div className="mb-4 flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="px-4 py-2 border rounded-md"
                  >
                    <option value="CAD">CAD ($)</option>
                    <option value="USD">USD ($)</option>
                    <option value="INR">INR (₹)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border border-gray-300 p-2 text-left">No.</th>
                        <th className="border border-gray-300 p-2 text-left">Item Description</th>
                        <th className="border border-gray-300 p-2 text-left">Qty</th>
                        <th className="border border-gray-300 p-2 text-left">Value ({currency})</th>
                        <th className="border border-gray-300 p-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={item.id}>
                          <td className="border border-gray-300 p-2">{index + 1}</td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              value={item.description}
                              onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                              placeholder="e.g., Cotton Clothing"
                              className="w-full"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(item.id, 'quantity', Math.max(0, e.target.value))}
                              placeholder="1"
                              className="w-20"
                              min="0"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.value}
                              onChange={(e) => updateItem(item.id, 'value', Math.max(0, e.target.value))}
                              placeholder="0.00"
                              className="w-28"
                              min="0"
                            />
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item.id)}
                              disabled={items.length <= 1}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 font-bold">
                      <tr>
                        <td colSpan="3" className="border border-gray-300 p-2 text-right">TOTAL DECLARED VALUE:</td>
                        <td className="border border-gray-300 p-2">{currency} {calculateTotal()}</td>
                        <td className="border border-gray-300 p-2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <Button onClick={addItem} variant="outline" className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item Row
                </Button>
              </div>

              {/* Declaration */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-navy-900 mb-4 border-b-2 border-red-600 pb-2">
                  SECTION D — CUSTOMER DECLARATION
                </h2>
                <div className="bg-gray-50 p-6 rounded-lg text-sm space-y-2">
                  <p>I, the undersigned sender, hereby declare and confirm that:</p>
                  <ol className="list-decimal list-inside space-y-1 text-gray-700">
                    <li>I personally know and have verified all shipment contents listed above.</li>
                    <li>All shipment contents have been accurately and truthfully declared.</li>
                    <li>No unknown, concealed, or intentionally undeclared items are included.</li>
                    <li>The shipment does not knowingly contain prohibited, illegal, or restricted items, including illegal drugs or narcotics.</li>
                    <li>I acknowledge that shipping restrictions and customs regulations apply.</li>
                    <li>I authorize lawful inspection of the shipment by Shippzy, carriers, customs, or other authorized authorities where permitted.</li>
                    <li>I accept responsibility for the accuracy of the information provided, subject to applicable law.</li>
                    <li>I understand that customs duties, taxes, inspections, and delays may apply.</li>
                  </ol>
                  <p className="text-xs text-gray-600 mt-4">Shippzy Inc. retains its own applicable legal and regulatory responsibilities.</p>
                </div>
              </div>

              {/* Signature */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-navy-900 mb-4 border-b-2 border-red-600 pb-2">
                  SECTION E — CUSTOMER SIGNATURE
                </h2>
                <div className="mb-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">I confirm that the information provided is accurate and I agree to the declaration above. *</span>
                  </label>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Full Name *</label>
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <Input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Signature *</label>
                  <div className="border-2 border-gray-300 rounded-md bg-white">
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{
                        className: 'w-full h-32 cursor-crosshair'
                      }}
                    />
                  </div>
                  <Button onClick={clearSignature} variant="outline" size="sm" className="mt-2">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Clear Signature
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 action-buttons">
                <Button onClick={handleSubmit} className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600">
                  <Send className="h-4 w-4 mr-2" />
                  Submit Declaration
                </Button>
                <Button onClick={handlePrint} variant="outline">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Form
                </Button>
                <Button onClick={handleDownloadPDF} variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button onClick={clearForm} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Clear Form
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Printable Version */}
      <div id="printable-form" ref={printRef} className="print-only" style={{ padding: '10mm', backgroundColor: 'white', fontSize: '10pt', lineHeight: '1.3' }}>
        <div style={{ textAlign: 'center', marginBottom: '15px', borderBottom: '2px solid #DC2626', paddingBottom: '10px' }}>
          <h1 style={{ fontSize: '18pt', fontWeight: 'bold', color: '#1e3a8a', margin: '0 0 5px 0' }}>SHIPPZY INC.</h1>
          <h2 style={{ fontSize: '14pt', fontWeight: 'bold', margin: 0 }}>CUSTOMS DECLARATION FORM</h2>
          <p style={{ fontSize: '8pt', color: '#666', margin: '5px 0 0 0' }}>140 Advance Blvd, Unit 7, Brampton, ON L6T 4Z8 | +1 (437) 616-0758</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <h3 style={{ fontSize: '11pt', fontWeight: 'bold', borderBottom: '1px solid #333', paddingBottom: '3px', marginBottom: '8px' }}>SENDER</h3>
            <div style={{ fontSize: '9pt', lineHeight: '1.5' }}>
              <p><strong>Name:</strong> {sender.fullName}</p>
              <p><strong>Phone:</strong> {sender.phone}</p>
              <p><strong>Email:</strong> {sender.email}</p>
              <p><strong>Address:</strong> {sender.address}</p>
              <p>{sender.city}, {sender.province}, {sender.postalCode}</p>
              <p>{sender.country}</p>
            </div>
          </div>
          <div>
            <h3 style={{ fontSize: '11pt', fontWeight: 'bold', borderBottom: '1px solid #333', paddingBottom: '3px', marginBottom: '8px' }}>RECEIVER</h3>
            <div style={{ fontSize: '9pt', lineHeight: '1.5' }}>
              <p><strong>Name:</strong> {receiver.fullName}</p>
              <p><strong>Phone:</strong> {receiver.phone}</p>
              <p><strong>Email:</strong> {receiver.email}</p>
              <p><strong>Address:</strong> {receiver.address}</p>
              <p>{receiver.city}, {receiver.province}, {receiver.postalCode}</p>
              <p>{receiver.country}</p>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <h3 style={{ fontSize: '11pt', fontWeight: 'bold', borderBottom: '1px solid #333', paddingBottom: '3px', marginBottom: '8px' }}>SHIPMENT CONTENTS</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6' }}>
                <th style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'left' }}>No.</th>
                <th style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'left' }}>Description</th>
                <th style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'left' }}>Qty</th>
                <th style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'left' }}>Value ({currency})</th>
              </tr>
            </thead>
            <tbody>
              {items.filter(item => item.description).map((item, index) => (
                <tr key={item.id}>
                  <td style={{ border: '1px solid #ccc', padding: '4px' }}>{index + 1}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px' }}>{item.description}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px' }}>{item.quantity}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px' }}>{item.value}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f3f4f6' }}>
                <td colSpan="3" style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'right' }}>TOTAL:</td>
                <td style={{ border: '1px solid #ccc', padding: '5px' }}>{currency} {calculateTotal()}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div style={{ marginBottom: '15px', fontSize: '8pt', lineHeight: '1.4' }}>
          <h3 style={{ fontSize: '10pt', fontWeight: 'bold', marginBottom: '5px' }}>DECLARATION</h3>
          <p style={{ marginBottom: '5px' }}>I declare that: (1) I have verified all contents; (2) All contents are accurately declared; (3) No undeclared items included; (4) No prohibited items included; (5) I authorize lawful inspection; (6) I accept responsibility for accuracy; (7) I understand customs regulations apply.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '20px' }}>
          <div>
            <p style={{ fontSize: '9pt', marginBottom: '5px' }}><strong>Customer Name:</strong></p>
            <p style={{ fontSize: '9pt', borderBottom: '1px solid #333', paddingBottom: '2px' }}>{customerName}</p>
          </div>
          <div>
            <p style={{ fontSize: '9pt', marginBottom: '5px' }}><strong>Signature:</strong></p>
            {signatureRef.current && !signatureRef.current.isEmpty() && (
              <img src={signatureRef.current.toDataURL()} alt="Signature" style={{ height: '30px', borderBottom: '1px solid #333' }} />
            )}
          </div>
          <div>
            <p style={{ fontSize: '9pt', marginBottom: '5px' }}><strong>Date:</strong></p>
            <p style={{ fontSize: '9pt', borderBottom: '1px solid #333', paddingBottom: '2px' }}>{date}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomsDeclarationFormPage;
